import { Link } from "react-router-dom";
import MarketingNav from "@/components/app/MarketingNav";
import SocialStudio from "@/components/app/SocialStudio";
import { Button } from "@/components/ui/button";
import { Sparkles, Linkedin, Twitter, Instagram, Facebook, AtSign, Cloud, ArrowRight } from "lucide-react";

const SUPPORTED = [
  { icon: Linkedin, name: "LinkedIn", note: "long-form, professional" },
  { icon: Twitter, name: "X / Twitter", note: "punchy + threads" },
  { icon: Instagram, name: "Instagram", note: "captions + hashtags" },
  { icon: Facebook, name: "Facebook", note: "conversational" },
  { icon: AtSign, name: "Threads", note: "raw, casual" },
  { icon: Cloud, name: "Bluesky", note: "smart, crisp" },
];

export default function SocialPage() {
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 border border-border bg-white px-3 py-1.5 text-xs tracking-wide" data-testid="social-page-badge">
            <Sparkles className="w-3.5 h-3.5" /> <span className="font-medium">Free · no signup required</span>
          </div>
          <h1 className="font-semibold tracking-tight mt-6 text-4xl md:text-6xl">
            Write a week of social posts<br />
            <span className="text-primary">in 30 seconds.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
            Turn one keyword, article, or URL into platform-ready posts for LinkedIn, X, Instagram,
            Facebook, Threads and Bluesky — with hashtags, multiple angles and a one-click X thread.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-px border border-border bg-border">
            {SUPPORTED.map((p) => (
              <div key={p.name} className="bg-white px-3 py-3 text-center" data-testid={`social-supported-${p.name.toLowerCase().replace(/\W+/g, "-")}`}>
                <p.icon className="w-4 h-4 mx-auto" strokeWidth={1.6} />
                <p className="font-semibold text-xs tracking-tight mt-1.5">{p.name}</p>
                <p className="font-mono-data text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{p.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GENERATOR */}
      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
          <SocialStudio mode="public" />
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-border bg-foreground text-background">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="font-mono-data text-xs uppercase tracking-widest text-background/60">// upgrade</p>
            <h2 className="font-semibold tracking-tight text-3xl md:text-4xl mt-2 max-w-xl">
              Want to repurpose your blog drafts?
            </h2>
            <p className="text-sm text-background/70 mt-2 max-w-lg">
              Inside ArloSEO, the Social tab pulls in any saved blog draft and rewrites it into platform-native posts — keeping you on the SEO + social treadmill in one workspace.
            </p>
          </div>
          <Link to="/register">
            <Button className="rounded-none h-12 px-6 bg-background text-foreground hover:bg-background/90" data-testid="social-cta-register">
              Start free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex items-center justify-between">
          <span className="font-semibold tracking-tight">ARLOSEO</span>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← Back to site</Link>
        </div>
      </footer>
    </div>
  );
}
